/**
 * Utility functions for managing cookies
 */

export const SELLER_FIRST_VISIT_COOKIE = 'seller_first_visit';

/**
 * Set a cookie with the given name, value, and options
 */
export const setCookie = (
  name: string,
  value: string,
  options: {
    path?: string;
    maxAge?: number;
    expires?: Date | string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
  } = {}
) => {
  const {
    path = '/',
    maxAge,
    expires,
    secure = process.env.NODE_ENV === 'production',
    sameSite = 'lax',
  } = options;

  let cookie = `${name}=${encodeURIComponent(value)}; path=${path}`;

  if (maxAge) {
    cookie += `; max-age=${maxAge}`;
  }

  if (expires) {
    cookie += `; expires=${expires instanceof Date ? expires.toUTCString() : expires}`;
  }

  if (secure) {
    cookie += '; secure';
  }

  cookie += `; samesite=${sameSite}`;

  document.cookie = cookie;
};

/**
 * Remove a cookie by setting its expiration to a past date
 */
export const removeCookie = (name: string, path = '/') => {
  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

/**
 * Check if a cookie exists
 */
export const hasCookie = (name: string): boolean => {
  return document.cookie.split(';').some((cookie) => {
    return cookie.trim().startsWith(`${name}=`);
  });
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Set the seller first visit cookie to true (user has visited the seller page)
 */
export const setSellerFirstVisitCookie = () => {
  setCookie(SELLER_FIRST_VISIT_COOKIE, 'true', {
    maxAge: 60 * 60 * 24 * 365, // 1 year
  });
};

/**
 * Set the seller first visit cookie to false (user has signed up)
 */
export const resetSellerFirstVisitCookie = () => {
  setCookie(SELLER_FIRST_VISIT_COOKIE, 'false', {
    maxAge: 60 * 60 * 24 * 365, // 1 year
  });
};

/**
 * Remove the seller first visit cookie (user has logged out)
 */
export const removeSellerFirstVisitCookie = () => {
  removeCookie(SELLER_FIRST_VISIT_COOKIE);
};
